import ChipMultiSelect from '@/components/ChipMultiSelect';
import PhonePicker from '@/components/PhonePicker';
import {
  EMAIL_MAX,
  NAME_MAX,
  NOTE_MAX,
  textInput,
  toggle,
  type EditValues,
} from '@/features/reservations/model/reservation-form';
import { useSpecialRequestLabels } from '@/features/reservations/special-request-labels';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { OCCASION_VALUES, SEATING_VALUES } from '@restaurant/api-contracts';
import { Block, BlockTitle, List, ListInput } from 'framework7-react';
import { useRef } from 'react';
import { useController, type Control } from 'react-hook-form';

interface Props {
  control: Control<EditValues, unknown, unknown>;
  savedPhone: string;
  onPhoneValid: (valid: boolean) => void;
}

export default function EditGuestFields({ control, savedPhone, onPhoneValid }: Props) {
  const t = useT();
  const { occasionLabel, seatingLabel } = useSpecialRequestLabels();
  // Restaurant country drives phone parsing so local-format numbers validate.
  const country = useRestaurantStore((s) => s.restaurantConfig?.country ?? undefined);
  const { field: name } = useController({ control, name: 'name' });
  const { field: email } = useController({ control, name: 'email' });
  const { field: phone } = useController({ control, name: 'phone' });
  // Options + values come straight from @restaurant/api-contracts (single source of
  // truth - can't drift); labels from `labels.ts`. Both pickers are multi-select;
  // seating also carries the former "special needs" options (chairs, access).
  const { field: occasion } = useController({ control, name: 'occasion' });
  const { field: seating } = useController({ control, name: 'seating' });
  const { field: internalNote } = useController({ control, name: 'internalNote' });
  const phoneAtOpen = useRef<string | null>(null);

  return (
    <>
      <BlockTitle>{t.editReservation.sectionGuest}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={t.editReservation.guestName}
          type="text"
          placeholder={t.editReservation.guestNamePlaceholder}
          maxlength={NAME_MAX}
          {...textInput(name)}
        />
        <ListInput
          label={t.editReservation.guestEmail}
          type="email"
          maxlength={EMAIL_MAX}
          placeholder="guest@example.com"
          {...textInput(email)}
        />
        <li>
          <PhonePicker
            label={t.editReservation.guestPhone}
            value={savedPhone}
            defaultCountry={country}
            placeholder="170 1234567"
            onChange={(e164, valid) => {
              phoneAtOpen.current ??= e164;
              onPhoneValid(valid);
              phone.onChange(e164 === phoneAtOpen.current ? savedPhone : e164);
            }}
          />
        </li>
      </List>

      <BlockTitle>{t.editReservation.sectionOccasion}</BlockTitle>
      <Block strongIos inset>
        <ChipMultiSelect
          options={OCCASION_VALUES}
          selected={occasion.value}
          onToggle={(o) => occasion.onChange(toggle(occasion.value, o))}
          getLabel={occasionLabel}
        />
      </Block>

      <BlockTitle>{t.editReservation.sectionSeating}</BlockTitle>
      <Block strongIos inset>
        <ChipMultiSelect
          options={SEATING_VALUES}
          selected={seating.value}
          onToggle={(s) => seating.onChange(toggle(seating.value, s))}
          getLabel={seatingLabel}
        />
      </Block>

      <BlockTitle>{t.editReservation.sectionInternal}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListInput
          label={t.editReservation.internalNote}
          type="textarea"
          maxlength={NOTE_MAX}
          placeholder={t.editReservation.notePlaceholder}
          {...textInput(internalNote)}
        />
      </List>
    </>
  );
}
